import React from 'react';
import { X, Palette, Trash2, Type, Zap, MoveHorizontal, Ghost, Rss, CloudSun, Clock, Globe, Thermometer } from 'lucide-react';
import { Switch } from '../../../components/ui/switch';
import { ScrollDirection } from './ScrollerPropertiesPanel';
import { FONT_FAMILIES } from '../../pages/template-editor/textEditorTypes';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

interface AppPropertiesSidepaneProps {
  selectedAppId: string;
  appName: string;
  settings: any;
  onSettingsChange: (updates: any) => void;
  onClose: () => void;
  onDelete: (id: string) => void;
}

const PRESET_COLORS = ['#FFFFFF', '#000000', '#30A84B', '#FF5F5F', '#5F5FFF', '#FFB800', '#7F56D9', '#2E90FA', '#12B76A', '#F79009'];

interface ColorPickerFieldProps {
  label: string;
  value: string;
  defaultColor: string;
  onChange: (color: string) => void;
  align?: 'start' | 'end';
}

const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  label,
  value,
  defaultColor,
  onChange,
  align = 'start'
}) => {
  const currentColor = value || defaultColor;

  return (
    <div className="flex flex-col gap-2">
      <Label className="font-sans font-semibold text-[#101928] text-[13px]">
        {label}
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2.5 h-[40px] px-2.5 bg-white border border-[#e4e7ec] rounded-[4px] hover:border-gray-300 transition-all text-left w-full overflow-hidden cursor-pointer"
          >
            <div
              className="w-5 h-5 rounded border border-gray-100 shrink-0 relative overflow-hidden cursor-pointer group-hover:scale-110 transition-transform"
              style={{ backgroundColor: currentColor }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="color"
                value={currentColor}
                onChange={(e) => onChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer scale-[3]"
              />
            </div>
            <span className="text-[11px] font-mono font-bold text-[#475367] uppercase truncate">
              {currentColor.replace('#', '')}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-3 bg-white border border-[#e4e7ec] shadow-xl rounded-xl z-[150]" align={align}>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-[4px] border border-[#e4e7ec] overflow-hidden shrink-0 bg-gray-50">
                <input
                  type="color"
                  value={currentColor}
                  onInput={(e) => onChange(e.currentTarget.value)}
                  className="absolute inset-0 w-[200%] h-[200%] -top-[50%] -left-[50%] cursor-pointer outline-none"
                />
              </div>
              <div className="flex-1">
                <Input
                  value={currentColor}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val && !val.startsWith('#')) val = '#' + val;
                    onChange(val);
                  }}
                  className="h-9 border-[#e4e7ec] focus-visible:border-[#30A84B] focus-visible:ring-0 text-[12px] font-mono font-bold rounded-[4px]"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-[#98a2b3] uppercase tracking-wider font-sans">Presets</p>
              <div className="grid grid-cols-5 gap-2">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onChange(c);
                    }}
                    className="w-full aspect-square rounded-[4px] border border-gray-100 hover:scale-110 active:scale-95 transition-all cursor-pointer"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const AppPropertiesSidepane: React.FC<AppPropertiesSidepaneProps> = ({
  selectedAppId,
  appName,
  settings,
  onSettingsChange,
  onClose,
  onDelete
}) => {
  const lowerName = appName.toLowerCase();
  const lowerId = selectedAppId.toLowerCase();

  const isScroller = lowerName.includes('scroller') || lowerId.includes('scroller') || settings?.category === 'Scroller';
  const isClock = lowerName.includes('clock') || lowerId.includes('clock') || settings?.category === 'Clock';
  const isWeather = lowerName.includes('weather') || lowerId.includes('weather') || settings?.category === 'Weather';
  const isRss = lowerName.includes('rss') || lowerName.includes('news') || lowerId.includes('rss') || lowerId.includes('news') || settings?.category === 'RSS';

  return (
    <div className="fixed right-0 top-0 w-[360px] h-screen bg-white shadow-2xl z-[100] flex flex-col border-l border-[#e4e7ec] animate-in slide-in-from-right duration-300">
      {/* Header - Matching TextApp NavHeader style */}
      <div className="h-[68px] flex items-center justify-between px-5 border-b border-[#e4e7ec] bg-white shrink-0">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="w-8 h-8 rounded-lg bg-[#30A84B]/10 flex items-center justify-center shrink-0">
            {isScroller ? (
              <Type className="w-4 h-4 text-[#30A84B]" />
            ) : isClock ? (
              <Clock className="w-4 h-4 text-[#30A84B]" />
            ) : isWeather ? (
              <CloudSun className="w-4 h-4 text-[#30A84B]" />
            ) : isRss ? (
              <Rss className="w-4 h-4 text-[#30A84B]" />
            ) : (
              <Palette className="w-4 h-4 text-[#30A84B]" />
            )}
          </div>
          <div>
            <h3 className="font-sans font-semibold text-[#1d2739] text-[16px] truncate">{appName}</h3>
            <p className="text-[10px] text-[#98a2b3] font-bold uppercase tracking-widest leading-none mt-0.5 font-sans">App Properties</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[#667185] hover:text-[#1d2739] transition-colors p-1.5 rounded-md hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-6 scrollbar-none bg-white">
        {/* Visuals Group (Available for all apps) */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Visuals</h4>
            <div className="h-px w-full bg-[#f0f2f5]" />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50/50 rounded-lg border border-[#e4e7ec]">
            <div className="space-y-0.5">
              <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                <Ghost className="w-3.5 h-3.5 text-[#30A84B]" />
                Transparent Background
              </Label>
              <p className="text-[11px] text-[#667185]">Remove solid color fill</p>
            </div>
            <Switch
              checked={settings.isTransparent || false}
              onCheckedChange={(checked: boolean) => onSettingsChange({ isTransparent: checked })}
            />
          </div>
        </div>

        {/* 1. SCROLLER APP SETTINGS */}
        {isScroller && (
          <div className="space-y-6">
            {/* Content & Typography Group */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Content & Style</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <Type className="w-3.5 h-3.5 text-[#30A84B]" />
                    Scroller Text
                  </Label>
                  <Textarea
                    value={settings.content || settings.text || ''}
                    onChange={(e) => onSettingsChange({ content: e.target.value, text: e.target.value })}
                    className="h-[100px] rounded-[4px] border-[#e4e7ec] bg-white px-3 py-2.5 text-[14px] placeholder:text-[#98a2b3] resize-none focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B] transition-all"
                    placeholder="Enter your scrolling message..."
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label className="font-sans font-semibold text-[#101928] text-[13px]">
                      Font Family
                    </Label>
                    <Select
                      value={settings.fontFamily || 'Bricolage Grotesque'}
                      onValueChange={(val) => onSettingsChange({ fontFamily: val })}
                    >
                      <SelectTrigger
                        className="h-[40px] rounded-[4px] border-[#e4e7ec] bg-white px-3 text-[14px] hover:border-gray-300 focus:ring-2 focus:ring-[#30A84B]/10 focus:border-[#30A84B] transition-all [&>svg:last-child]:text-[#667185]"
                      >
                        <SelectValue>
                          <span style={{ fontFamily: settings.fontFamily || 'Bricolage Grotesque' }}>
                            {settings.fontFamily || 'Bricolage Grotesque'}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        sideOffset={4}
                        className="max-h-[300px] w-[var(--radix-select-trigger-width)] rounded-[4px] border-[#e4e7ec] bg-white shadow-xl z-[9999]"
                      >
                        {FONT_FAMILIES.map(font => (
                          <SelectItem
                            key={font}
                            value={font}
                            className="text-[14px] py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B] data-[state=checked]:bg-[#30A84B]/10 data-[state=checked]:text-[#30A84B]"
                          >
                            <span style={{ fontFamily: font }}>{font}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="font-sans font-semibold text-[#101928] text-[13px]">
                      Size (px)
                    </Label>
                    <Input
                      type="number"
                      value={settings.fontSize || 24}
                      onChange={(e) => onSettingsChange({ fontSize: parseInt(e.target.value) || 1 })}
                      className="h-[40px] rounded-[4px] border-[#e4e7ec] bg-white px-3 text-[14px] focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Animation Group */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Animation</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <Zap className="w-3.5 h-3.5 text-[#30A84B]" />
                    Animation Speed
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={settings.scrollSpeed || 'medium'}
                    onValueChange={(val) => {
                      if (!val) return;
                      const speedMap: Record<string, number> = { slow: 2, medium: 8, fast: 24 };
                      onSettingsChange({
                        scrollSpeed: val,
                        speed: speedMap[val]
                      });
                    }}
                    className="h-[38px] p-1 bg-[#f0f2f5]/50 rounded-[4px] border border-[#e4e7ec] w-full"
                  >
                    <ToggleGroupItem
                      value="slow"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Slow
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="medium"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Medium
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="fast"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Fast
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <MoveHorizontal className="w-3.5 h-3.5 text-[#30A84B]" />
                    Direction
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={settings.direction || 'rtl'}
                    onValueChange={(val) => val && onSettingsChange({ direction: val as ScrollDirection })}
                    className="h-[38px] p-1 bg-[#f0f2f5]/50 rounded-[4px] border border-[#e4e7ec] w-full"
                  >
                    <ToggleGroupItem
                      value="rtl"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Right to Left
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="ltr"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Left to Right
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Colors</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorPickerField
                  label="Text"
                  value={settings.textColor}
                  defaultColor="#FFFFFF"
                  onChange={(color) => onSettingsChange({ textColor: color })}
                  align="start"
                />
                <ColorPickerField
                  label="Background"
                  value={settings.backgroundColor}
                  defaultColor="#002B5B"
                  onChange={(color) => onSettingsChange({ backgroundColor: color })}
                  align="end"
                />
              </div>
            </div>
          </div>
        )}

        {/* 2. RSS / NEWS APP SETTINGS */}
        {isRss && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Feed Configuration</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <Rss className="w-3.5 h-3.5 text-[#30A84B]" />
                    RSS Feed URL
                  </Label>
                  <Input
                    value={settings.rss_feed_url || settings.feedUrl || ''}
                    onChange={(e) => onSettingsChange({ rss_feed_url: e.target.value, feedUrl: e.target.value })}
                    className="h-[40px] rounded-[4px] border-[#e4e7ec] bg-white px-3 text-[14px] focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B]"
                    placeholder="https://feeds.bbci.co.uk/news/rss.xml"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="font-sans font-semibold text-[#101928] text-[13px]">
                    Display Format
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={settings.rss_display_type || settings.displayUnit || 'ticker'}
                    onValueChange={(val) => {
                      if (!val) return;
                      onSettingsChange({
                        rss_display_type: val,
                        displayUnit: val,
                        gridPreview: val === 'grid'
                      });
                    }}
                    className="h-[38px] p-1 bg-[#f0f2f5]/50 rounded-[4px] border border-[#e4e7ec] w-full"
                  >
                    <ToggleGroupItem
                      value="ticker"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Ticker / Ribbon
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="grid"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Cards / Grid
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="font-sans font-semibold text-[#101928] text-[13px]">
                    Slide Duration (seconds)
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={settings.duration || 10}
                    onChange={(e) => onSettingsChange({ duration: e.target.value })}
                    className="h-[40px] rounded-[4px] border-[#e4e7ec] bg-white px-3 text-[14px] focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B]"
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Colors</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorPickerField
                  label="Text"
                  value={settings.textColor}
                  defaultColor="#FFFFFF"
                  onChange={(color) => onSettingsChange({ textColor: color })}
                  align="start"
                />
                <ColorPickerField
                  label="Background"
                  value={settings.backgroundColor}
                  defaultColor="#002B5B"
                  onChange={(color) => onSettingsChange({ backgroundColor: color })}
                  align="end"
                />
              </div>
            </div>
          </div>
        )}

        {/* 3. WEATHER APP SETTINGS */}
        {isWeather && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Weather Location</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <CloudSun className="w-3.5 h-3.5 text-[#30A84B]" />
                    City / Location
                  </Label>
                  <Input
                    value={settings.location || settings.CurrentLocation || ''}
                    onChange={(e) => onSettingsChange({ location: e.target.value, CurrentLocation: e.target.value })}
                    className="h-[40px] rounded-[4px] border-[#e4e7ec] bg-white px-3 text-[14px] focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B]"
                    placeholder="e.g. Dubai, London, New York"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <Thermometer className="w-3.5 h-3.5 text-[#30A84B]" />
                    Temperature Unit
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={settings.temperature || 'celsius'}
                    onValueChange={(val) => val && onSettingsChange({ temperature: val })}
                    className="h-[38px] p-1 bg-[#f0f2f5]/50 rounded-[4px] border border-[#e4e7ec] w-full"
                  >
                    <ToggleGroupItem
                      value="celsius"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Celsius (°C)
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="fahrenheit"
                      className="flex-1 h-full rounded-[4px] text-[10px] font-bold uppercase tracking-wider data-[state=on]:bg-white data-[state=on]:text-[#30A84B] transition-all border-none font-sans"
                    >
                      Fahrenheit (°F)
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Colors</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorPickerField
                  label="Text"
                  value={settings.textColor}
                  defaultColor="#FFFFFF"
                  onChange={(color) => onSettingsChange({ textColor: color })}
                  align="start"
                />
                <ColorPickerField
                  label="Background"
                  value={settings.backgroundColor}
                  defaultColor="#002B5B"
                  onChange={(color) => onSettingsChange({ backgroundColor: color })}
                  align="end"
                />
              </div>
            </div>
          </div>
        )}

        {/* 4. CLOCK APP SETTINGS */}
        {isClock && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Clock Settings</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <Globe className="w-3.5 h-3.5 text-[#30A84B]" />
                    Time Zone
                  </Label>
                  <Input
                    value={settings.time_zone || settings.TimeZone || ''}
                    onChange={(e) => onSettingsChange({ time_zone: e.target.value, TimeZone: e.target.value })}
                    className="h-[40px] rounded-[4px] border-[#e4e7ec] bg-white px-3 text-[14px] focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B]"
                    placeholder="e.g. Asia/Dubai, UTC, America/New_York"
                  />
                  <p className="text-[11px] text-[#667185]">Standard IANA timezone name (e.g. UTC, Asia/Dubai, Europe/London)</p>
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Colors</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorPickerField
                  label="Text"
                  value={settings.textColor}
                  defaultColor="#FFFFFF"
                  onChange={(color) => onSettingsChange({ textColor: color })}
                  align="start"
                />
                <ColorPickerField
                  label="Background"
                  value={settings.backgroundColor}
                  defaultColor="#002B5B"
                  onChange={(color) => onSettingsChange({ backgroundColor: color })}
                  align="end"
                />
              </div>
            </div>
          </div>
        )}

        {/* 5. GENERIC APP / OTHER WIDGET SETTINGS */}
        {!isScroller && !isRss && !isWeather && !isClock && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">App Content</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-2 font-sans font-semibold text-[#101928] text-[13px]">
                    <Type className="w-3.5 h-3.5 text-[#30A84B]" />
                    Text / Content
                  </Label>
                  <Textarea
                    value={settings.content || settings.text || settings.description || ''}
                    onChange={(e) => onSettingsChange({ content: e.target.value, text: e.target.value, description: e.target.value })}
                    className="h-[100px] rounded-[4px] border-[#e4e7ec] bg-white px-3 py-2.5 text-[14px] placeholder:text-[#98a2b3] resize-none focus-visible:ring-2 focus-visible:ring-[#30A84B]/10 focus-visible:border-[#30A84B] transition-all"
                    placeholder="Enter app content or text..."
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Colors</h4>
                <div className="h-px w-full bg-[#f0f2f5]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorPickerField
                  label="Text"
                  value={settings.textColor}
                  defaultColor="#FFFFFF"
                  onChange={(color) => onSettingsChange({ textColor: color })}
                  align="start"
                />
                <ColorPickerField
                  label="Background"
                  value={settings.backgroundColor}
                  defaultColor="#002B5B"
                  onChange={(color) => onSettingsChange({ backgroundColor: color })}
                  align="end"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-5 border-t border-[#e4e7ec] bg-gray-50/30">
        <button
          type="button"
          onClick={() => onDelete(selectedAppId)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-[#f04438]/20 bg-white hover:bg-red-50 text-[#f04438] transition-all font-semibold text-sm shadow-sm font-sans"
        >
          <Trash2 className="w-4 h-4" />
          Delete Widget
        </button>
      </div>
    </div>
  );
};
